import { isNotStageP } from "src/app/utils/storebrand-staging";
import { parseFeatureEnvCookie, serializeFeatureEnvs } from "./feature-env-cookie";
import { BRANCH_PATTERN, FEATURE_ENV_QUERY_PARAM, FeatureEnv, REPO_PATTERN } from "./feature-env.model";
import { FeatureEnvService } from "./feature-env.service";

/** The parts of `window` this needs, so tests do not have to fake the real one. */
export interface DeepLinkWindow {
  readonly location: Pick<Location, "href"> & { replace(url: string): void };
  readonly history: Pick<History, "replaceState">;
}

/**
 * Applies `?features=repo:branch` and reloads into that environment.
 *
 * The parameter is always removed from the URL before reloading, which is what
 * stops the reload from looping. Ignored entirely in production: a parameter
 * that reroutes traffic has no business being live on the public site.
 */
export function applyFeatureEnvFromUrl(service: FeatureEnvService, win: DeepLinkWindow): void {
  if (!isNotStageP()) {
    return;
  }

  const url = new URL(win.location.href);
  const raw = url.searchParams.get(FEATURE_ENV_QUERY_PARAM);
  if (raw === null) {
    return;
  }

  url.searchParams.delete(FEATURE_ENV_QUERY_PARAM);
  const cleanedUrl = url.toString();

  const requested = parseFeatureEnvCookie(raw).filter(
    (env) => REPO_PATTERN.test(env.repo) && BRANCH_PATTERN.test(env.branch),
  );
  const isNoop = requested.length === 0 || serializeFeatureEnvs(requested) === serializeFeatureEnvs(service.active());

  if (isNoop) {
    // Nothing to do, but the parameter should not linger in the address bar
    // or get shared onward by a copy of the URL.
    win.history.replaceState({}, "", cleanedUrl);
    return;
  }

  // The link is authoritative: clear any repo present in the current state but
  // not in the requested set, then apply each requested environment.
  const active = service.active();
  const requestedRepos = new Set(requested.map((env) => env.repo));
  active.forEach((env: FeatureEnv) => {
    if (!requestedRepos.has(env.repo)) {
      service.clear(env.repo);
    }
  });
  requested.forEach((env: FeatureEnv) => service.apply(env.repo, env.branch));
  win.location.replace(cleanedUrl);
}
